import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { apiGet, apiPath, apiUpload } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AccountingBalance {
  id:             string;
  asOfDate:       string;
  pcecCode:       string;
  pcecSubChapter: string | null;
  pcecChapter:    PcecChapter;
  pcecLabel:      string | null;
  currency:       string;
  balance:        string;  // BigDecimal as string
  balanceType:    'ASSET' | 'LIABILITY';
  bsPosition:     'BILAN' | 'HORS_BILAN';
  source:         string | null;
  uploadedAt:     string;
}

export interface ChapterSummary {
  pcecChapter:    PcecChapter;
  currency:       string;
  totalAsset:     string;
  totalLiability: string;
}

export interface UploadResponse {
  rowsWritten:   number;
  primaryDate:   string;
  distinctDates: number;
  rowsByChapter: Record<string, number>;
}

export type PcecChapter =
  | 'TRESORERIE'
  | 'CREDITS'
  | 'TITRES'
  | 'DEPOTS'
  | 'DERIVES'
  | 'HORS_BILAN'
  | 'AUTRES';

export const CHAPTER_LABELS: Record<PcecChapter, string> = {
  TRESORERIE: 'Trésorerie & interbancaire',
  CREDITS:    'Crédits',
  TITRES:     'Titres',
  DEPOTS:     'Dépôts & ressources',
  DERIVES:    'Produits dérivés',
  HORS_BILAN: 'Engagements hors-bilan',
  AUTRES:     'Autres',
};

export const CHAPTER_ORDER: PcecChapter[] = [
  'TRESORERIE', 'CREDITS', 'TITRES', 'DEPOTS', 'DERIVES', 'HORS_BILAN', 'AUTRES',
];

// ── Query keys ────────────────────────────────────────────────────────────────

const KEYS = {
  detail:  (date?: string) => ['accounting-balances', 'detail', date] as const,
  summary: (date?: string, ccy?: string) => ['accounting-balances', 'summary', date, ccy] as const,
};

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useAccountingBalances(
  date?: string,
  options?: Omit<UseQueryOptions<AccountingBalance[], CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  const url = apiPath('alm', 'data', 'accounting-balances') + (date ? `?date=${date}` : '');
  return useQuery<AccountingBalance[], CubixApiError>({
    queryKey: KEYS.detail(date),
    queryFn:  () => apiGet<AccountingBalance[]>(url),
    ...options,
  });
}

export function useAccountingBalanceSummary(
  date?: string,
  currency?: string,
  options?: Omit<UseQueryOptions<ChapterSummary[], CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  const params = new URLSearchParams();
  if (date)     params.set('date', date);
  if (currency) params.set('currency', currency);
  const url = apiPath('alm', 'data', 'accounting-balances', 'summary')
            + (params.toString() ? `?${params}` : '');
  return useQuery<ChapterSummary[], CubixApiError>({
    queryKey: KEYS.summary(date, currency),
    queryFn:  () => apiGet<ChapterSummary[]>(url),
    ...options,
  });
}

export function useUploadAccountingBalances() {
  const qc = useQueryClient();
  return useMutation<UploadResponse, CubixApiError, File>({
    mutationFn: (file) =>
      apiUpload<UploadResponse>(
        apiPath('alm', 'data', 'accounting-balances'),
        file,
        {},
        'file',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting-balances'] }),
  });
}
